import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import PasswordInput from "@/components/PasswordInput";
import logo from "@/assets/logo.png";

const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function CustomerAuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(() => (searchParams.get("mode") === "inscription" ? "register" : "login"));
  useEffect(() => { if (searchParams.get("mode") === "inscription") setMode("register"); }, [searchParams]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email, origin_url: window.location.origin });
        setForgotSent(true);
      } else if (mode === "login") {
        await login(form.email, form.password);
        navigate("/compte");
      } else {
        await register(form.name, form.email, form.password);
        navigate("/compte");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setForgotSent(false);
  };

  return (
    <div data-testid="customer-auth-page" className="mx-auto flex max-w-md flex-col items-center px-4 py-16">
      <div className="w-full rounded-2xl border border-amber-800/15 bg-white p-8 shadow-lg shadow-amber-900/5">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src={logo} alt="Pétales de Miel" data-testid="customer-auth-logo" className="h-14 w-14 object-contain" />
          <h1 className="font-serif text-3xl font-semibold text-bark">
            {mode === "forgot" ? "Mot de passe oublié" : "Votre espace gourmand"}
          </h1>
          <p className="text-sm text-bark-soft">
            {mode === "forgot"
              ? "Recevez un lien de réinitialisation par e-mail."
              : "Suivez vos commandes et retrouvez vos coordonnées."}
          </p>
        </div>

        {mode !== "forgot" && (
          <div className="mb-6 grid grid-cols-2 gap-1 rounded-full bg-sand p-1">
            <button
              data-testid="tab-login"
              onClick={() => switchMode("login")}
              className={`rounded-full py-2.5 text-sm font-medium transition-all ${mode === "login" ? "bg-white text-bark shadow-sm" : "text-bark-soft"}`}
            >
              Se connecter
            </button>
            <button
              data-testid="tab-register"
              onClick={() => switchMode("register")}
              className={`rounded-full py-2.5 text-sm font-medium transition-all ${mode === "register" ? "bg-white text-bark shadow-sm" : "text-bark-soft"}`}
            >
              Créer un compte
            </button>
          </div>
        )}

        {mode === "forgot" && forgotSent ? (
          <div data-testid="forgot-sent" className="space-y-4 text-center">
            <CheckCircle2 size={40} className="mx-auto text-sage" />
            <p className="text-sm leading-relaxed text-bark-soft">
              Si un compte existe avec <strong className="text-bark">{form.email}</strong>, un e-mail contenant un lien
              de réinitialisation vient d'être envoyé. Le lien est valable 1 heure.
            </p>
            <button
              data-testid="forgot-back-login"
              onClick={() => switchMode("login")}
              className="w-full rounded-full bg-amber-deep py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98]"
            >
              Retour à la connexion
            </button>
          </div>
        ) : (
          <form data-testid="customer-auth-form" onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label htmlFor="cu-name" className="mb-1.5 block text-sm font-medium text-bark">Votre nom</label>
                <input id="cu-name" data-testid="cu-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} placeholder="Marie Dupont" />
              </div>
            )}
            <div>
              <label htmlFor="cu-email" className="mb-1.5 block text-sm font-medium text-bark">Email</label>
              <input id="cu-email" data-testid="cu-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="marie@exemple.fr" />
            </div>
            {mode !== "forgot" && (
              <div>
                <label htmlFor="cu-password" className="mb-1.5 block text-sm font-medium text-bark">Mot de passe</label>
                <PasswordInput id="cu-password" data-testid="cu-password" required minLength={mode === "register" ? 8 : undefined} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} placeholder={mode === "register" ? "8 caractères minimum" : "••••••••"} />
                {mode === "login" && (
                  <button
                    type="button"
                    data-testid="forgot-password-link"
                    onClick={() => switchMode("forgot")}
                    className="mt-2 text-xs font-medium text-amber-deep transition-colors hover:text-bark"
                  >
                    Mot de passe oublié ?
                  </button>
                )}
              </div>
            )}

            {error && (
              <p data-testid="customer-auth-error" className="rounded-xl bg-terra/10 px-4 py-3 text-sm font-medium text-terra">
                {error}
              </p>
            )}

            <button
              data-testid="customer-auth-submit"
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-amber-deep py-3.5 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? "Un instant…" : mode === "login" ? "Me connecter" : mode === "register" ? "Créer mon compte" : "Recevoir le lien"}
            </button>

            {mode === "forgot" && (
              <button
                type="button"
                data-testid="forgot-cancel"
                onClick={() => switchMode("login")}
                className="w-full text-center text-xs font-medium text-bark-soft transition-colors hover:text-amber-deep"
              >
                Retour à la connexion
              </button>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
